import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import __isEmpty from 'lodash/isEmpty';
import Imatge from 'components/3cat/Imatge';
import Icona from '../ui/Icona';
import styles from './videobasic.module.scss';

const VideoBasic = (props) => {
    const {
        urlMp4,
        urlWebm,
        urlWmv,
        urlOgv,
        playOnView = true,
        tresholdPlayOnView = 50,
        estil,
        isActive = true,
        imatges,
        titol,
        onEnded
    } = props;
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [isInView, setIsInView] = useState(!playOnView);
    const [hasEnded, setHasEnded] = useState(false);
    const [playbackProgress, setPlaybackProgress] = useState(0);
    const [hasDuration, setHasDuration] = useState(false);

    const updatePlaybackProgress = () => {
        const video = videoRef.current;

        if (!video || !Number.isFinite(video.duration) || video.duration <= 0) {
            setHasDuration(false);
            setPlaybackProgress(0);
            return;
        }

        setHasDuration(true);
        const nextProgress = (video.currentTime / video.duration) * 100;
        setPlaybackProgress(Math.min(Math.max(nextProgress, 0), 100));
    };

    const stylesPlayer = clsx({
        [styles.content]: true,
        [styles[estil]]: !!estil
    });

    const stylesVideoWrapper = clsx({
        [styles.videoWrapper]: true
    });

    const ringStyle = {
        '--progress': hasDuration ? playbackProgress : 100,
        '--progress-color': hasDuration && isPlaying ? 'var(--clr_portal_100)' : '#ffffff'
    };

    const handlePlayPause = (event) => {
        event.stopPropagation();
        event.preventDefault();

        const video = videoRef.current;

        if (!video) {
            return;
        }

        if (hasEnded) {
            video.currentTime = 0;
            setPlaybackProgress(0);
            setHasEnded(false);
            video.play();
            setIsPlaying(true);
            return;
        }

        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }

        setIsPlaying(!isPlaying);
    };

    const handleMute = (event) => {
        event.stopPropagation();
        event.preventDefault();

        const video = videoRef.current;

        if (!video) {
            return;
        }

        const nextIsMuted = !isMuted;
        video.muted = nextIsMuted;
        setIsMuted(nextIsMuted);
    };

    const handleReload = (event) => {
        event.stopPropagation();
        event.preventDefault();

        const video = videoRef.current;

        if (!video) {
            return;
        }

        video.currentTime = 0;
        video.muted = isMuted;
        setPlaybackProgress(0);
        setHasEnded(false);
        setIsPlaying(true);
        video.play();
    };

    useEffect(() => {
        if (!playOnView || typeof IntersectionObserver !== 'function') {
            return;
        }

        const video = videoRef.current;

        if (!video) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInView(entry.isIntersecting);
            },
            {
                threshold: tresholdPlayOnView / 100
            }
        );

        observer.observe(video);

        return () => observer.disconnect();
    }, [playOnView, tresholdPlayOnView]);

    useEffect(() => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        const handleTimeUpdate = () => {
            updatePlaybackProgress();
        };

        const handleLoadedMetadata = () => {
            updatePlaybackProgress();
        };

        const handleEnded = () => {
            setHasEnded(true);
            setIsPlaying(false);
            setHasDuration(true);
            setPlaybackProgress(100);
            onEnded?.();
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('ended', handleEnded);

        if (!isActive || (playOnView && !isInView)) {
            video.pause();
            setIsPlaying(false);
            return () => {
                video.removeEventListener('timeupdate', handleTimeUpdate);
                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                video.removeEventListener('ended', handleEnded);
            };
        }

        if (!hasEnded) {
            video.play();
            setIsPlaying(true);
        }

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('ended', handleEnded);
        };
    }, [isActive, isInView, playOnView, hasEnded, hasDuration]);

    useEffect(() => {
        const video = videoRef.current;

        if (!video || !isActive || !hasEnded) {
            return;
        }

        video.currentTime = 0;
        setPlaybackProgress(0);
        setHasDuration(false);
        setHasEnded(false);
        setIsPlaying(true);
        video.play();
    }, [isActive, hasEnded]);

    if (!urlMp4 && !urlWebm && !urlOgv && !urlWmv) {
        return null;
    }

    return (
        <div className={styles.videoAnimat}>
            <div className={stylesPlayer}>
                <div className={stylesVideoWrapper}>
                    {hasEnded && imatges && (
                        <div className={styles.posterOverlay} aria-hidden='true'>
                            <Imatge
                                imatges={imatges}
                                tipus='web_destacat'
                                alt={titol || 'Video'}
                                layout='fill'
                                objectFit='cover'
                                crop='1903'
                                className={styles.posterImage}
                            />
                        </div>
                    )}

                    <video
                        ref={videoRef}
                        autoPlay={!playOnView}
                        playsInline
                        muted={isMuted}
                        style={{ opacity: hasEnded ? 0.3 : 1 }}
                    >
                        {urlMp4 && <source src={urlMp4} type='video/mp4' />}
                        {urlWebm && <source src={urlWebm} type='video/webm' />}
                        {urlWmv && <source src={urlWmv} type='video/wmv' />}
                        {urlOgv && <source src={urlOgv} type='video/ogv' />}
                    </video>

                    <div className={styles.controls}>
                        {!hasEnded ? (
                            <>
                                <button
                                    type='button'
                                    onClick={handlePlayPause}
                                    className={styles.botoPlayPause}
                                    style={ringStyle}
                                >
                                    {!isPlaying ? (
                                        <Icona
                                            className={styles.icona}
                                            icona='play'
                                            alt='Reprodueix el video'
                                            isBlanc={true}
                                            inline={true}
                                        />
                                    ) : (
                                        <Icona
                                            className={`${styles.icona} ${styles.reproduccioVideo}`}
                                            icona='pause_ple'
                                            alt='Aturar el video'
                                            isBlanc={true}
                                            inline={true}
                                        />
                                    )}
                                </button>
                                <button type='button' onClick={handleMute} className={styles.botoPlayPause}>
                                    {isMuted ? (
                                        <Icona
                                            className={styles.icona}
                                            icona='novolum'
                                            alt='Activar el so del video'
                                            isBlanc={true}
                                            inline={true}
                                        />
                                    ) : (
                                        <Icona
                                            className={styles.icona}
                                            icona='volum'
                                            alt='Silenciar el video'
                                            isBlanc={true}
                                            inline={true}
                                        />
                                    )}
                                </button>
                            </>
                        ) : (
                            <button type='button' onClick={handleReload} className={styles.botoPlayPause} style={ringStyle}>
                                <Icona
                                    className={styles.icona}
                                    icona='tornaaveure'
                                    alt='Reproduir de nou'
                                    isBlanc={true}
                                    inline={true}
                                />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoBasic;
